import React, { Component } from 'react';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { findIndex, get } from 'lodash-es';
import axios from 'axios';
import ReactLoading from 'react-loading';

import AdditionalCharges from './AdditionalCharges';
import ChargeModal from './ChargeModal';
import Contacts from './Contacts';
import GeographicShipping from './GeographicShipping';
import InvoiceSettings from './InvoiceSettings';
import PaymentOptions from './PaymentOptions';
import PostSaleTo from './PostSaleTo';
import TermsAndConditions from './TermsAndConditions';
import InlineAlert from '../../../../utilities/form-components/InlineAlert';

const errorLoadingText = 'Unable to load your sale details. Please try again later or contact Customer Care any time at:';
const errorSubmittingText = 'Unable to save sale details. Please try again later or contact Customer Care any time at:';

class Step1 extends Component {
  state = {
    errorLoading: false,
    errorSubmitting: false,
    loading: true,
    chargeData: {},
    chargeIndex: -1,
    showChargeModal: false,
    validation: {
      // invoice validation
      bccEmail: null,
      fromEmail: null,
      fromName: null,
      subject: null
    }
  };

  async componentDidMount() {
    const {
      house: { houseID, houseRef },
      match: {
        params: { catalogID }
      }
    } = this.props;

    try {
      // First need to call the summary to get the groupID and invoiceGroupSettingsID
      const summaryResponse = await axios(
        `/cps/app/catalog/${catalogID}?_=${new Date().getTime()}`
      );

      const sessionDefinition = get(summaryResponse, 'data.data.sessionDefinition', null);
      const groupID = get(sessionDefinition, 'groupID', '');
      const invoiceGroupSettingsID = get(sessionDefinition, 'invoiceGroupSettingsID', '');
      const currency = get(sessionDefinition, 'currency', { code: '', name: '' });
      const selectedContacts = get(sessionDefinition, 'contacts', {});

      const urls = [
        `/cps/app/house/${houseID}/contacts?_=${new Date().getTime()}`,
        '/app/country',
        `/app/house/${houseRef}/invoiceGroupSettings?groupSettingsID=${invoiceGroupSettingsID}&_=${new Date().getTime()}`,
        `/app/house/${houseRef}/bin/group/${groupID}/payments?_=${new Date().getTime()}`,
        `/app/house/${houseRef}/bin/group/${groupID}/shipTo?_=${new Date().getTime()}`,
        `/app/house/${houseRef}/bin/group/${groupID}/terms?_=${new Date().getTime()}`
      ];

      const [
        contactResponse,
        countryResponse,
        groupSettingsResponse,
        paymentResponse,
        shippingResponse,
        termsResponse
      ] = await Promise.all(urls.map(url => axios(url)));

      // Parse Responses
      const allContacts = get(contactResponse, 'data.data.contacttypes', []);

      // Default the selected contact to the first for each contact if they weren't set already
      allContacts.forEach(contact => {
        if (!selectedContacts[contact.id]) {
          selectedContacts[contact.id] = contact.contacts[0].id;
        }
      });

      const countries = get(countryResponse, 'data.data.countries');
      // List of country codes that we want to bring to the top
      const importantCountries = ['UK', 'US', 'CA', 'AU'];
      // Prepend list with the All Countries option and European Union option
      const allCountriesOption = { countryCode: 'ALL', countryName: 'All Countries' };
      const euCountriesOption = { countryCode: 'EU', countryName: 'European Union' };

      const firstCountries = countries.filter(
        country => importantCountries.indexOf(country.countryCode) >= 0
      );
      const remainingCountries = countries.filter(
        country => importantCountries.indexOf(country.countryCode) === -1
      );
      const filteredCountries = [
        { ...allCountriesOption },
        { ...euCountriesOption },
        ...firstCountries,
        ...remainingCountries
      ];

      const groupSettings = get(groupSettingsResponse, 'data.data.groupSettings', null);
      // BE gives us taxCountry field but wants the taxCountryID field back?
      groupSettings.taxSettings = groupSettings.taxSettings.map(item => ({
        taxCountryID: item.taxCountry,
        taxRate: item.formattedTaxRate,
        taxStateID: item.taxStateID,
        taxType: item.taxType
      }));

      const paymentMethods = get(paymentResponse, 'data.data.paymentMethods', []);

      const shipData = get(shippingResponse, 'data.data.binShipTo', { exclude: [], include: [] });
      // Add checkbox selected property to shipData
      shipData.exclude.forEach(exclude => (exclude.selected = false));
      shipData.include.forEach(include => (include.selected = false));

      const terms = get(termsResponse, 'data.data.terms', []);
      // Default first two terms to open in the accordion
      terms.forEach((term, index) => (term.isOpen = index <= 1));

      this.setState({
        allContacts,
        countries: filteredCountries,
        currency,
        groupID,
        groupSettings,
        loading: false,
        paymentMethods,
        selectedContacts,
        shipData,
        terms
      });
    } catch {
      this.setState({
        loading: false,
        errorLoading: true
      });
    }
  }

  handleDeleteCharge = index => {
    const groupSettings = { ...this.state.groupSettings };
    groupSettings.taxSettings.splice(index, 1);
    this.setState({ groupSettings });
  };

  handleInvoiceChange = ({ target: { id, value } }) => {
    const groupSettings = { ...this.state.groupSettings };
    const validation = { ...this.state.validation };
    validation[id] = null;
    groupSettings[id] = value;
    this.setState({ groupSettings, validation });
  };

  handlePaymentMethodCheckbox = card => {
    const paymentMethods = [...this.state.paymentMethods];
    const paymentIndex = paymentMethods.findIndex(payment => payment.id === card.id);
    if (paymentIndex === -1) {
      paymentMethods.push(card);
    } else {
      paymentMethods.splice(paymentIndex, 1);
    }
    this.setState({ paymentMethods });
  };

  handleSelectContact = (e, contactType) => {
    const selectedContacts = { ...this.state.selectedContacts };
    selectedContacts[contactType] = parseInt(e.target.value, 10);
    this.setState({ selectedContacts });
  };

  handleShippingMoveItems = direction => {
    let exclude = [...this.state.shipData.exclude];
    let include = [...this.state.shipData.include];
    if (direction === 'left') {
      const selectedItems = exclude.filter(item => item.selected);
      exclude = exclude.filter(item => !item.selected);
      selectedItems.forEach(item => (item.selected = false));
      include = include
        .concat(selectedItems)
        .sort((a, b) => (a.countryName > b.countryName ? 1 : -1));
    } else {
      const selectedItems = include.filter(item => item.selected);
      include = include.filter(item => !item.selected);
      selectedItems.forEach(item => (item.selected = false));
      exclude = exclude
        .concat(selectedItems)
        .sort((a, b) => (a.countryName > b.countryName ? 1 : -1));
    }

    this.setState({
      shipData: {
        exclude,
        include
      }
    });
  };

  handleShippingSelectAll = type => {
    if (type === 'include') {
      const include = [...this.state.shipData.include];
      if (include.every(item => item.selected)) {
        include.forEach(item => (item.selected = false));
      } else {
        include.forEach(item => (item.selected = true));
      }
      this.setState({
        shipData: {
          ...this.state.shipData,
          include
        }
      });
    } else {
      const exclude = [...this.state.shipData.exclude];
      if (exclude.every(item => item.selected)) {
        exclude.forEach(item => (item.selected = false));
      } else {
        exclude.forEach(item => (item.selected = true));
      }
      this.setState({
        shipData: {
          ...this.state.shipData,
          exclude
        }
      });
    }
  };

  handleShippingSelectItem = (e, type) => {
    const {
      target: { name }
    } = e;

    if (type === 'include') {
      const include = [...this.state.shipData.include];
      const val = findIndex(include, item => item.countryCode === name);
      include[val].selected = !include[val].selected;
      this.setState({
        shipData: {
          ...this.state.shipData,
          include
        }
      });
    } else {
      const exclude = [...this.state.shipData.exclude];
      const val = findIndex(exclude, item => item.countryCode === name);
      exclude[val].selected = !exclude[val].selected;
      this.setState({
        shipData: {
          ...this.state.shipData,
          exclude
        }
      });
    }
  };

  handleSubmitChargeModal = (chargeData, chargeIndex) => {
    const groupSettings = { ...this.state.groupSettings };
    if (chargeIndex >= 0) {
      groupSettings.taxSettings[chargeIndex] = chargeData;
    } else {
      groupSettings.taxSettings.push(chargeData);
    }
    this.setState({ groupSettings, showChargeModal: false });
  };

  handleTermsCheckbox = index => {
    const terms = [...this.state.terms];
    terms[index].active = !terms[index].active;
    // If the user selects the checkbox - also trigger collapse behavior
    terms[index].isOpen = terms[index].active;
    this.setState({ terms });
  };

  handleTermsInput = (e, index) => {
    const terms = [...this.state.terms];
    const { id, value } = e.target;
    terms[index][id] = value;
    this.setState({ terms });
  };

  submitStepOne = async () => {
    const {
      house: { houseRef },
      location: { state },
      match: {
        params: { catalogID }
      }
    } = this.props;

    const {
      groupID,
      groupSettings: {
        bccEmail,
        fromEmail,
        fromName,
        groupName,
        houseAddressID,
        invoiceGroupID,
        subject,
        taxSettings
      },
      paymentMethods,
      selectedContacts,
      shipData,
      terms
    } = this.state;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step5';

    const groupSettingsPostData = {
      bccEmail,
      fromEmail,
      fromName,
      groupName,
      houseAddressID,
      invoiceGroupID,
      subject,
      taxSettings
    };

    const requestArray = [
      {
        url: `/cps/app/catalog/${catalogID}/contacts/independent`,
        postData: { contacts: selectedContacts }
      },
      {
        url: `/app/house/${houseRef}/invoiceGroupSettings?_=${new Date().getTime()}`,
        postData: groupSettingsPostData
      },
      {
        url: `/app/house/${houseRef}/bin/group/${groupID}/shipTo?include=false?_=${new Date().getTime()}`,
        postData: shipData.exclude.map(item => item.countryCode)
      },
      {
        url: `/app/house/${houseRef}/bin/group/${groupID}/terms?_=${new Date().getTime()}`,
        postData: terms
      },
      {
        url: `/app/house/${houseRef}/bin/group/${groupID}/payments?_=${new Date().getTime()}`,
        postData: paymentMethods
      }
    ];

    const postHeaders = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    };

    try {
      await Promise.all(
        requestArray.map(request => axios.post(request.url, request.postData, postHeaders))
      );

      let stepNumber = 5;
      if (!isEditingSession) {
        // User is going through BIN DIY their first time - update step tracker to step 2
        stepNumber = 2;
        await axios.post(`/cps/app/diy-step-tracker/${catalogID}`, stepNumber, postHeaders);
      }

      // Navigate to either step 2 if new sale, or back to step 5 if editing sale
      this.props.history.push(`/session/bin/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`);
    } catch {
      window.scrollTo(0, 0);
      this.setState({ errorSubmitting: true });
    }
  };

  toggleAccordion = index => {
    const terms = [...this.state.terms];
    // Only trigger collapse behavior, user may not want to select this
    terms[index].isOpen = !terms[index].isOpen;
    this.setState({ terms });
  };

  toggleChargeModal = (chargeIndex = -1) => {
    const { groupSettings, showChargeModal } = this.state;
    const chargeData = chargeIndex >= 0 ? groupSettings.taxSettings[chargeIndex] : {};
    this.setState({ chargeIndex, chargeData, showChargeModal: !showChargeModal });
  };

  validateEmail = e => {
    const {
      target: { id, value }
    } = e;

    // eslint-disable-next-line
    const validEmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (value.length > 0 && !validEmailRegex.test(value)) {
      const validation = { ...this.state.validation };
      validation[id] = 'Please enter a valid email address';
      this.setState({ validation });
    }
  };

  validateRequired = e => {
    const {
      target: { id, name, value }
    } = e;
    if (!value) {
      const validation = { ...this.state.validation };
      validation[id] = `${name} is required`;
      this.setState({ validation });
    }
  };

  render() {
    const { loading } = this.state;

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    const { errorLoading } = this.state;

    // Could not load data from API
    if (errorLoading) {
      return <InlineAlert baseText={errorLoadingText} />;
    }

    const {
      allContacts,
      chargeData,
      chargeIndex,
      countries,
      currency,
      errorSubmitting,
      groupSettings,
      paymentMethods,
      selectedContacts,
      shipData,
      showChargeModal,
      terms,
      validation
    } = this.state;

    const {
      location: { state },
      match: {
        params: { catalogID, groupID }
      }
    } = this.props;

    // Get edit permutation info
    const isEditingSession = get(state, 'linkedFrom', null) === 'step5';

    // Check validation to disable button
    const paymentsValid = paymentMethods.length >= 1;
    const invoiceValid = get(groupSettings, 'fromEmail', '').length >= 1
      && get(groupSettings, 'fromName', '').length >= 1
      && get(groupSettings, 'subject', '').length >= 1;
    const termsValid = get(terms, '[0].termText', '').length >= 1
      && get(terms, '[0].termTitle', '').length >= 1
      && get(terms, '[1].termText', '').length >= 1
      && get(terms, '[1].termTitle', '').length >= 1;

    const isFormValid = paymentsValid && invoiceValid && termsValid;

    return (
      <div>
        {errorSubmitting && <InlineAlert baseText={errorSubmittingText} />}
        <p>* = Required</p>

        <PaymentOptions
          currency={currency}
          handlePaymentMethodCheckbox={this.handlePaymentMethodCheckbox}
          paymentMethods={paymentMethods}
        />

        <PostSaleTo />

        <Contacts
          handleSelectContact={this.handleSelectContact}
          allContacts={allContacts}
          selectedContacts={selectedContacts}
        />

        <AdditionalCharges
          groupSettings={groupSettings}
          handleDeleteCharge={this.handleDeleteCharge}
          toggleChargeModal={this.toggleChargeModal}
        />

        <GeographicShipping
          handleShippingMoveItems={this.handleShippingMoveItems}
          handleShippingSelectAll={this.handleShippingSelectAll}
          handleShippingSelectItem={this.handleShippingSelectItem}
          shipData={shipData}
        />

        <InvoiceSettings
          groupSettings={groupSettings}
          handleInvoiceChange={this.handleInvoiceChange}
          validation={validation}
          validateEmail={this.validateEmail}
          validateRequired={this.validateRequired}
        />

        <TermsAndConditions
          handleTermsCheckbox={this.handleTermsCheckbox}
          handleTermsInput={this.handleTermsInput}
          terms={terms}
          toggleAccordion={this.toggleAccordion}
        />

        <ChargeModal
          chargeIndex={chargeIndex}
          chargeData={chargeData}
          countries={countries}
          handleSubmitChargeModal={this.handleSubmitChargeModal}
          showChargeModal={showChargeModal}
          toggleChargeModal={this.toggleChargeModal}
        />

        <div className="mt-4">
          <Button
            className={!isFormValid ? 'cursor-not-allowed' : ''}
            color="primary"
            disabled={!isFormValid}
            onClick={() => this.submitStepOne()}
          >
            {isEditingSession ? 'Save & Return to Summary' : 'Continue'}
          </Button>

          <Link
            className="ml-5"
            to={isEditingSession
              ? `/session/bin/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/5`
              : '/my-auctions/current'}
          >
            {isEditingSession ? 'Cancel & Return to Summary' : 'Cancel'}
          </Link>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ house }) => ({
  house
});

export default withRouter(
  connect(
    mapStateToProps,
    null
  )(Step1)
);
